import React from 'react';
import { Jumbotron } from '@scholastic/volume-react';
import './StatsHelp.scss';

const StatsHelp = () => {
  return (
    <div className="stats-help">
      <div className="stats-help__text-container stats-help__text-container--nobackground">
        <Jumbotron tag="div">
          <h1 className="stats-help__text-container__heading">Overview</h1>
          <p className="stats-help__text-container__content">
            There are two main types of reports that can be generated for your product usage
            statistics: summary reports and detailed reports. A description of each is provided
            below.
          </p>
          <h2 className="stats-help__text-container__sub-heading">
            <span>Summary Reports</span>
          </h2>
          <p className="stats-help__text-container__content">
            These reports provide a summative total based on the time period selected and allow to
            select more than one product at a time. They are most useful if you wish to:
          </p>
          <ul className="stats-help__text-container__content--list">
            <li>
              <span>
                Get a quick snapshot of your product usage for one or more products at a time
              </span>
            </li>
            <li>
              <span>Select a specific time period for which you would like to retrieve stats</span>
            </li>
          </ul>
          <p className="stats-help__text-container__content">
            Summary reports can be viewed online or you can choose to have a .csv file downloaded to
            your computer. There are three types:
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Overall summary report &ndash;
            </span>{' '}
            This report allows you to get a summative total of the usage stats for the product(s)
            you select.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Individual Site Report &ndash;{' '}
            </span>{' '}
            If you are logged in as a school or district administrator, this report will allow you
            to retrieve the summative usage statistics at the individual site level.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              User Group Report &ndash;{' '}
            </span>
            If you are logged in as a school or district administrator and you have specified groups
            for your sites, this report will allow to you run the report for one or more groups.
          </p>
          <p className="stats-help__text-container__content">
            <span>&nbsp;</span>
          </p>
          <h2 className="stats-help__text-container__sub-heading">
            <span>Detailed Reports</span>
          </h2>
          <p className="stats-help__text-container__content">
            These reports provide a monthly total based on the 15 month previous to the month
            selected. These reports can only be run for a single product at a time. These reports
            are most useful if you wish to:
          </p>
          <ul className="stats-help__text-container__content--list">
            <li>
              Get a detailed view of the product usage broken down by month over a 15 month period
            </li>
            <li>See a graphical representation of the usage over a 15 month period.</li>
          </ul>
          <p className="stats-help__text-container__content">
            There are six types of detailed reports:
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Detailed usage report &ndash;{' '}
            </span>
            This report provides you with a month-by-month breakdown of usage for a 15 month period
            of time and includes the data for documents, sessions, and searches (where applicable).
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Detailed Document usage &ndash;{' '}
            </span>{' '}
            This report provides you with a month-by-month report of only the documents viewed for a
            15 month period.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Detailed Session usage &ndash;{' '}
            </span>
            This report provides you with a month-by-month report of the sessions for a 15 month
            period.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Document Usage by Group &ndash;
            </span>{' '}
            If you are logged in as a school or district administrator and you have specified groups
            for your sites, this report will provide you with a monthly breakdown of the documents
            viewed for one or more groups for a 15 month period.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Session Usage by Group &ndash;{' '}
            </span>
            If you are logged in as a school or district administrator and you have specified groups
            for your sites, this report will provide you with a monthly breakdown of the sessions
            for one or more groups for a 15 month period.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Individual Site Report &ndash;{' '}
            </span>
            If you are logged in as a school or district administrator this report will provide you
            with a monthly breakdown of the documents, sessions, and searches (where applicable)
            viewed for one or more individual sites for a 15 month period.
          </p>
          <h1 className="stats-help__text-container__heading">Generating Reports</h1>
          <p className="stats-help__text-container__content">
            When you log into the site, the Standard Tab is displayed by default. From this tab you
            can choose the type of report that you would like to generate.
          </p>
          <h3 className="stats-help__text-container__content--tab">
            <span className="stats-help__text-container__content--span">Standard Tab</span>
          </h3>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Select the report you wish to generate.
            </span>
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Select the product(s) you wish to have included in the report.
            </span>
            Summary reports allow you to select multiple products at a time; detailed reports are
            limited to a single product selection.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Select your date (range).
            </span>
            Summary reports allow you to specific a specific date range for your report. Detailed
            reports are generated using the 15 months prior to the date you select.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Include Individual Site statistics in your report
            </span>
            <em className="stats-help__text-container__content--italic">
              . (This option is only available if you have selected the Overall Summary, Detailed
              Usage, Detailed Document Usage, and Detailed Session Usage reports.)
            </em>
            If you are logged in as a school or district administrator, this option allows you to
            view the summative stats for your site as well as any sites that fall under your
            particular organization. If you wish to view the usage for your site only, uncheck this
            option.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">Select your Output.</span>
            This option allows you to view your usage statistics onscreen or to download a CSV
            version of the report.
            <em className="stats-help__text-container__content--italic">
              (Note: the download option for the detailed reports does not include the graphical
              representation of the data that appears in the onscreen report.)
            </em>
          </p>
          <p className="stats-help__text-container__content">
            Click the
            <span className="stats-help__text-container__content--span"> CREATE REPORT </span>
            button for Overall Summary Report, Detailed Usage, Detailed Document Usage, and Detailed
            Session Usage reports.
          </p>
          <p className="stats-help__text-container__content">
            Click the <span className="stats-help__text-container__content--span">NEXT </span>
            button for Individual or Group site reports. You will be taken to another page where you
            can choose Sites or Groups to include/exclude from your report. By default, all
            sites/groups are selected. Click Create Report to generate your report data.
          </p>
          <h3 className="stats-help__text-container__content--tab">
            <span className="stats-help__text-container__content--span">Counter Tab</span>
          </h3>
          <p className="stats-help__text-container__content">
            There are two COUNTER 2 reports available. These reports will generate a CSV file that
            contain the summative total of usage for a specific year, broken down by month, for each
            product to which you subscribe. Only full months will show in this report so, for
            example, if you ran the report in the middle of March, the last month that would display
            in the report would be February.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Book Report 2 (R1) - Number of Successful Section Requests by Month and Title
              &ndash;&nbsp;
            </span>
            This report shows the number of documents retrieved &nbsp;for each product.
          </p>
          <p className="stats-help__text-container__content">
            <span className="stats-help__text-container__content--span">
              Book Report 5 (R1) - Total Searches and Sessions by Month and Title&nbsp;
            </span>
            &ndash; This report shows the number of sessions and searches recorded for each product
            (where appropriate).
          </p>
          <h3 className="stats-help__text-container__content--tab">
            <span className="stats-help__text-container__content--span">Monthly Tab</span>
          </h3>
          <p className="stats-help__text-container__content">
            Users can sign up to have their stats reports emailed to them automatically at the
            beginning of each month for the month prior.
          </p>
          <p className="stats-help__text-container__content">
            Click on the Monthly Tab and select the report(s) you wish to receive from the list
            provided. Enter your email address in the form field provided and click the{' '}
            <span className="stats-help__text-container__content--span">SUBMIT&nbsp;</span>button.
          </p>
        </Jumbotron>
      </div>
    </div>
  );
};

export default StatsHelp;
